
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-white text-brand-green-dark hover:bg-brand-stone-light hover:text-brand-green-dark",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-2 border-brand-green text-brand-green-dark bg-transparent hover:bg-brand-green hover:text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-brand-green-dark hover:bg-accent hover:text-brand-green",
        link: "text-brand-green underline-offset-4 hover:underline",
        primary: "bg-brand-green text-white hover:bg-brand-green-light focus:ring-brand-green",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        md: "px-5 py-2.5",
        lg: "h-11 rounded-md px-8 text-lg py-3.5",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  fullWidth?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  href?: string
  as?: React.ElementType
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    asChild = false,
    fullWidth = false,
    icon,
    iconPosition = 'left',
    href,
    as: Component = asChild ? Slot : 'button',
    ...props 
  }, ref) => {
    const width = fullWidth ? 'w-full' : '';
    
    // If this is a link (has href), use an anchor tag
    if (href) {
      return (
        <a 
          href={href}
          className={cn(
            buttonVariants({ variant, size }),
            width,
            // Special case for buttons on green backgrounds (like in CTA sections)
            variant === 'outline' && className?.includes('border-white') ? 
              'border-white border-2 text-white font-semibold hover:bg-white hover:text-brand-green-dark' : '',
            
            className
          )}
        >
          {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
          {props.children}
          {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
        </a>
      );
    }
    
    return (
      <Component
        className={cn(
          buttonVariants({ variant, size }), 
          width, 
          // Special case for buttons on green backgrounds (like in CTA sections)
          variant === 'outline' && className?.includes('border-white') ? 
            'border-white border-2 text-white font-semibold hover:bg-white hover:text-brand-green-dark' : '',
          
          className
        )}
        ref={ref}
        {...props}
      >
        {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
        {props.children}
        {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
      </Component>
    );
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
